import { Link } from "react-router-dom";
import { Building2, MapPin, Radio, Users } from "lucide-react";
import { StatusBadge } from "@/components/businesses/StatusBadge";
import { SafeImage } from "@/components/ui/SafeImage";
import { deriveConnectedServiceStatus, type BusinessListEntry } from "@/lib/business-analytics";
import { formatNumber, imageSrc } from "@/lib/utils";

function BusinessLogo({ entry }: { entry: BusinessListEntry }) {
  const logo = imageSrc(entry.logo);
  if (logo) {
    return (
      <SafeImage
        src={logo}
        alt={entry.name ?? "Business"}
        width={48}
        height={48}
        className="h-12 w-12 rounded-2xl object-cover"
      />
    );
  }

  const initial = entry.name?.charAt(0)?.toUpperCase() || "B";
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent-subtle text-sm font-bold text-accent-foreground">
      {initial}
    </div>
  );
}

export function BusinessCard({ entry }: { entry: BusinessListEntry }) {
  const status = deriveConnectedServiceStatus(entry.connectedService);

  return (
    <Link
      to={`/businesses/${entry.id}`}
      className="group hover-raise block rounded-3xl bg-canvas p-5 hover:bg-surface"
    >
      <div className="flex items-start gap-3">
        <BusinessLogo entry={entry} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-extrabold tracking-tight text-foreground">
              {entry.name || "Untitled business"}
            </p>
            {entry.isShftOriginal ? (
              <span className="rounded-full bg-foreground px-2 py-0.5 text-[10px] font-bold text-accent">
                Shft Original
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm font-semibold text-muted">{entry.type || "Unspecified type"}</p>
          <p className="mt-1 flex items-center gap-1 text-xs text-muted">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{entry.country || entry.address || "No location"}</span>
          </p>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-xs font-semibold text-muted">
        <span className="inline-flex items-center gap-1">
          <Users className="h-3.5 w-3.5" />
          {formatNumber(entry.customerCount)} customers
        </span>
        <span className="inline-flex items-center gap-1">
          <Radio className="h-3.5 w-3.5" />
          {formatNumber(entry.subscriberCount)} subscribers
        </span>
        <span className="inline-flex items-center gap-1">
          <Building2 className="h-3.5 w-3.5" />
          {entry.users?.length ?? 0} team
        </span>
      </div>
    </Link>
  );
}
